// src/App.js
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';

import AuthPage from './pages/AuthPage';
import ResetPage from './pages/ResetPage';
import Dashboard from './pages/Dashboard';
import SiteAppConfigurePage from './pages/SiteAppConfigurePage';
import ChatSettingsPage from './pages/ChatSettingsPage';
import LandingPage from './pages/LandingPage';
import RouteSuspenseFallback from './components/RouteSuspenseFallback';
import { Box } from '@mui/material';
import { AppUiProvider } from './context/AppUiContext';
import { PlatformSettingsProvider } from './context/PlatformSettingsContext';
import { IdleSessionProvider } from './context/IdleSessionContext';
import ThemeModeToggle from './components/ThemeModeToggle';
import PlatformErrorBoundary from './components/PlatformErrorBoundary';
import TelemetryRouteSync from './components/TelemetryRouteSync';
import { isBackOfficeHostname } from './lib/domainRouting';

const SuperAdmin = lazy(() => import('./pages/SuperAdmin'));
const CompanyBuilderWizard = lazy(() => import('./pages/CompanyBuilderWizard'));
const ChatEmbedPage = lazy(() => import('./pages/ChatEmbedPage'));

/** Auth-style routes keep centered padding; dashboard routes fill the viewport. */
function AuthLayout({ children }) {
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '100vh',
        paddingTop: '5vh',
      }}
    >
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
        <ThemeModeToggle />
      </Box>
      {children}
    </Box>
  );
}

/** Public landing host (no admin subdomain). Embed is registered separately. */
function LandingRoutes() {
  return (
    <Routes>
      <Route path="*" element={<LandingPage />} />
    </Routes>
  );
}

/** Back Office host. Embed is registered separately (naked). */
function BackOfficeRoutes() {
  return (
    <Suspense fallback={<RouteSuspenseFallback />}>
      <Routes>
      {/* Legacy URL — prefer Operations → Chat Settings in the dashboard sidebar */}
      <Route path="/admin/chat-settings" element={<ChatSettingsPage />} />
      <Route path="/dashboard/chat-settings" element={<Dashboard />} />
      <Route path="/dashboard/*" element={<Dashboard />} />
      <Route path="/ai-studio" element={<Dashboard />} />
      <Route path="/company-builder" element={<CompanyBuilderWizard />} />
      <Route path="/admin/companies" element={<SuperAdmin />} />
      <Route path="/admin/support-inbox" element={<SuperAdmin />} />
      <Route path="/super-admin/support-inbox" element={<SuperAdmin />} />
      <Route path="/admin" element={<SuperAdmin />} />
      <Route path="/site-app/:appId/configure" element={<SiteAppConfigurePage />} />
      <Route
        path="/login"
        element={
          <AuthLayout>
            <AuthPage />
          </AuthLayout>
        }
      />
      <Route
        path="/"
        element={
          <AuthLayout>
            <AuthPage />
          </AuthLayout>
        }
      />
      <Route
        path="/reset"
        element={
          <AuthLayout>
            <ResetPage />
          </AuthLayout>
        }
      />
    </Routes>
    </Suspense>
  );
}

/**
 * Authenticated / marketing app shell (platform settings, theme, telemetry).
 * Never wraps /embed/chat.
 */
function HostedAppShell() {
  const isBackOffice = isBackOfficeHostname();

  if (isBackOffice) {
    return (
      <AppUiProvider>
        <PlatformSettingsProvider>
          <IdleSessionProvider>
            <PlatformErrorBoundary>
              <TelemetryRouteSync />
              <BackOfficeRoutes />
            </PlatformErrorBoundary>
          </IdleSessionProvider>
        </PlatformSettingsProvider>
      </AppUiProvider>
    );
  }

  return (
    <AppUiProvider>
      <PlatformErrorBoundary>
        <LandingRoutes />
      </PlatformErrorBoundary>
    </AppUiProvider>
  );
}

function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <Routes>
        {/*
          Public embed: ThemeProvider/CssBaseline only (via AppUiProvider).
          No PlatformSettings, Workspace, Auth, or other data-fetching contexts.
        */}
        <Route
          path="/embed/chat"
          element={
            <AppUiProvider>
              <PlatformErrorBoundary>
                <Suspense fallback={<RouteSuspenseFallback />}>
                  <ChatEmbedPage />
                </Suspense>
              </PlatformErrorBoundary>
            </AppUiProvider>
          }
        />
        <Route path="*" element={<HostedAppShell />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
